import { useState, useCallback, useRef } from "react";
import { Order } from "@/types/supabase";
import { fetchWithAuth } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";

interface UseHistoricalOrdersParams {
  startDate?: string;
  endDate?: string;
  branchId?: number;
  brandId?: number; // Add brandId parameter
  status?: string;
  searchTerm?: string;
  page?: number;
  pageSize?: number;
}

interface UseHistoricalOrdersReturn {
  items: Order[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  hasMore: boolean;
  fetchOrders: (params: UseHistoricalOrdersParams) => Promise<void>;
  refetch: () => Promise<void>;
}

export const useHistoricalOrders = (): UseHistoricalOrdersReturn => {
  // Cache for brand -> branches to avoid repeated network calls
  const brandBranchesCache = useRef<Map<number, number[]>>(new Map());
  const [items, setItems] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentParams, setCurrentParams] = useState<UseHistoricalOrdersParams>(
    {},
  );
  const { getAccessToken, userRole, supabaseInfo } = useAuth();
  // Build permission filters from AuthContext (supabaseInfo.branchIds)
  const basePermissionFilters = {
    canAccessAll: userRole === "admin" || !supabaseInfo?.branchIds?.length,
    accessibleBusinessIds: [],
    accessibleBranchIds: (supabaseInfo?.branchIds as number[]) || [],
    accessibleBrandIds: (supabaseInfo?.brandIds as number[]) || [],
  } as {
    canAccessAll: boolean;
    accessibleBusinessIds: number[];
    accessibleBranchIds: number[];
    accessibleBrandIds: number[];
  };

  const fetchOrders = useCallback(
    async (params: UseHistoricalOrdersParams) => {
      try {
        setLoading(true);
        setError(null);

        // Build query params for API
        const queryParams: Record<string, any> = {};

        // Pagination
        queryParams.page = params.page || 1;
        queryParams.pageSize = params.pageSize || 50;

        // Dates
        if (params.startDate) {
          queryParams.startDate = params.startDate;
        }
        if (params.endDate) {
          // Use end of day for endDate
          const endDate = new Date(params.endDate);
          endDate.setHours(23, 59, 59, 999);
          queryParams.endDate = endDate.toISOString();
        }

        // Filters
        if (params.brandId) {
          queryParams.brandId = params.brandId;
        }
        if (params.branchId) {
          queryParams.branchId = params.branchId;
        }
        if (params.status && params.status !== "all") {
          queryParams.status = params.status;
        }
        if (params.searchTerm && params.searchTerm.trim() !== "") {
          queryParams.searchTerm = params.searchTerm.trim();
        }

        // Build query string
        const queryString = Object.entries(queryParams)
          .map(
            ([key, value]) =>
              `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
          )
          .join("&");

        // Permissions: Only allow if user has access to this branch/brand
        // (Mimic previous logic: check branch/brand access)
        if (params.branchId) {
          if (
            !(
              basePermissionFilters.canAccessAll ||
              basePermissionFilters.accessibleBranchIds.includes(
                params.branchId,
              )
            )
          ) {
            setItems([]);
            setTotalCount(0);
            setCurrentParams(params);

            return;
          }
        }
        if (params.brandId) {
          // Get all branches for this brand (from cache or API)
          let brandBranchIds =
            brandBranchesCache.current.get(params.brandId) || null;

          if (!brandBranchIds) {
            const token = await getAccessToken();
            const response = await fetchWithAuth(
              `/branch/branches?brandId=${params.brandId}&page=1&limit=50`,
              token,
            );
            const data = Array.isArray(response)
              ? response
              : response?.data || [];

            if (data && data.length > 0) {
              brandBranchIds = (data as Array<{ id: number }>).map(
                (branch) => branch.id,
              );
              brandBranchesCache.current.set(params.brandId, brandBranchIds);
            }
          }

          if (brandBranchIds && brandBranchIds.length > 0) {
            let allowedBranchIds: number[];
            if (basePermissionFilters.canAccessAll) {
              allowedBranchIds = brandBranchIds;
            } else {
              allowedBranchIds = brandBranchIds.filter((branchId: number) =>
                basePermissionFilters.accessibleBranchIds.includes(branchId),
              );
            }
            if (
              params.branchId &&
              !allowedBranchIds.includes(params.branchId)
            ) {
              setItems([]);
              setTotalCount(0);
              setCurrentParams(params);
              return;
            }
            if (!params.branchId && allowedBranchIds.length === 0) {
              setItems([]);
              setTotalCount(0);
              setCurrentParams(params);
              return;
            }
          } else {
            setItems([]);
            setTotalCount(0);
            setCurrentParams(params);

            return;
          }
        }

        // Fetch from API
        const token = await getAccessToken();
        const url = `/order/orders?${queryString}`;
        const response = await fetchWithAuth(url, token);

        // Response can have multiple shapes depending on backend:
        // - { data: Order[], totalCount: number }
        // - { data: Order[], total: number, page: string | number, limit: string | number, totalPages: number }
        const data = response?.data || [];
        const totalCount =
          typeof response?.totalCount === "number"
            ? response.totalCount
            : typeof response?.total === "number"
              ? response.total
              : Array.isArray(data)
                ? data.length
                : 0;

        if (queryParams.page === 1) {
          setItems(data || []);
        } else {
          setItems((prev) => {
            const newItems = [...prev, ...(data || [])];
            return newItems;
          });
        }

        setTotalCount(totalCount);
        setCurrentParams(params);
      } catch (err) {
        console.error("Error in fetchHistoricalOrders:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Error al cargar el historial de órdenes",
        );
      } finally {
        setLoading(false);
      }
    },
    [basePermissionFilters],
  );

  const refetch = useCallback(async () => {
    if (Object.keys(currentParams).length > 0) {
      await fetchOrders(currentParams);
    }
  }, [currentParams, fetchOrders]);

  const hasMore = items.length < totalCount;

  return {
    items,
    loading,
    error,
    totalCount,
    hasMore,
    fetchOrders,
    refetch,
  };
};
